/**
 * IMPORTS
 */
import {fetch as fetchCustomer} from 'src/aggregates/customers/api';
import {modify as updateCustomer} from 'src/aggregates/customers/api';
import customersSelectors from 'src/aggregates/customers/selectors';
import {CUSTOMER_TAG_ADDED} from 'src/aggregates/customers/subscriptions';
import {CUSTOMER_TAG_REMOVED} from 'src/aggregates/customers/subscriptions';
import {updateKanbanMetadata} from 'src/aggregates/user/api';
import {fetch as getUser} from 'src/aggregates/user/selectors';
import {getKanbanMetadata} from 'src/aggregates/user/selectors';
import {logger} from 'src/logger';
import graphis from 'src/services/graphis';


/**
 * TYPES
 */
import {ICustomerTagAdded}
    from 'src/aggregates/customers/subscriptions/index.d';
import {ICustomerTagAddedArgs}
    from 'src/aggregates/customers/subscriptions/index.d';
import {ICustomerTagRemoved}
    from 'src/aggregates/customers/subscriptions/index.d';
import {ICustomerTagRemovedArgs}
    from 'src/aggregates/customers/subscriptions/index.d';
import {IPayload} from 'src/services/graphis/index.d';


/**
 * CODE
 */

/**
 * I subscribe on customer-tag-added event.
 *
 * :param tag: tag id
 *
 * :returns: nothing
 */
function subscribeCustomerTagAdded(tag: number): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // subscribe to customer-tag-added topic
    graphis.subscribe<IPayload<ICustomerTagAdded>, ICustomerTagAddedArgs>(
        async (payload) =>
        {
            // get payload data
            const {
                customer: id,
                customerTag,
                tag,
            } = payload.data.customerTagAdded;

            // get stored customer
            const customer = customersSelectors.fetch(id) ?? null;

            // customer isn't in store: fetch data
            if (customer === null)
            {
                await fetchCustomer(id);
            }

            // customer is in store: update tags
            else
            {
                // check if payload tag already exist in stored customer
                const alreadyExist = customer.tags
                    .find(customerTag => customerTag.tag === tag) !== undefined;

                // set updated customer tags
                const tags = alreadyExist === true ?
                    customer.tags :
                    [...customer.tags, {id: customerTag, tag}];

                // update customer on store
                await updateCustomer(
                    customer.address1,
                    customer.address2,
                    customer.agents,
                    customer.city,
                    customer.code,
                    customer.contacts,
                    customer.description,
                    customer.document,
                    customer.id,
                    customer.name,
                    customer.organization,
                    customer.state,
                    customer.zipcode,
                    tags,
                );
            }

            // get column customers
            const customers = getKanbanMetadata().byTag[tag]?.customers ?? [];

            // customer isn't in list: push to list
            if (customers.includes(id) === false)
            {
                customers.push(id);
            }

            // update customers list
            updateKanbanMetadata(tag, {customers});
        },
        CUSTOMER_TAG_ADDED,
        {tag},
    );

    // log subscription
    logger.info(
        'CVJPZ0094I',
        {
            company,
            name,
            tag,
            topic: CUSTOMER_TAG_ADDED.getTopic({tag}),
            user,
        },
    );
}


/**
 * I subscribe on customer-tag-removed event.
 *
 * :param tag: tag id
 *
 * :returns: nothing
 */
function subscribeCustomerTagRemoved(tag: number): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // subscribe to customer-tag-removed topic
    graphis.subscribe<IPayload<ICustomerTagRemoved>, ICustomerTagRemovedArgs>(
        async (payload) =>
        {
            // get payload data
            const {
                customer: id,
                customerTag,
                tag,
            } = payload.data.customerTagRemoved;

            // get stored customer
            const customer = customersSelectors.fetch(id) ?? null;

            // customer found: update tags
            if (customer !== null)
            {
                await updateCustomer(
                    customer.address1,
                    customer.address2,
                    customer.agents,
                    customer.city,
                    customer.code,
                    customer.contacts,
                    customer.description,
                    customer.document,
                    customer.id,
                    customer.name,
                    customer.organization,
                    customer.state,
                    customer.zipcode,
                    customer.tags.filter(tag => tag.id !== customerTag),
                );
            }

            // remove customer id of kanban column
            const customers = getKanbanMetadata().byTag[tag]?.customers ?? [];
            updateKanbanMetadata(
                tag,
                {
                    customers: customers.filter(customer => customer !== id),
                },
            );
        },
        CUSTOMER_TAG_REMOVED,
        {tag},
    );

    // log subscription
    logger.info(
        'CVJPZ0094I',
        {
            company,
            name,
            tag,
            topic: CUSTOMER_TAG_REMOVED.getTopic({tag}),
            user,
        },
    );
}


/**
 * I unsubscribe on customer-tag-added event.
 *
 * :param tag: tag id
 *
 * :returns: nothing
 */
function unsubscribeCustomerTagAdded(tag: number): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // unsubscribe to customer-tag-added topic
    graphis.unsubscribe(CUSTOMER_TAG_ADDED, {tag});

    // log unsubscribe
    logger.info(
        'CVJPZ0095I',
        {
            company,
            name,
            tag,
            topic: CUSTOMER_TAG_ADDED.getTopic({tag}),
            user,
        },
    );
}


/**
 * I unsubscribe on customer-tag-removed event.
 *
 * :param tag: tag id
 *
 * :returns: nothing
 */
function unsubscribeCustomerTagRemoved(tag: number): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // unsubscribe to customer-tag-removed topic
    graphis.unsubscribe(CUSTOMER_TAG_REMOVED, {tag});

    // log unsubscribe
    logger.info(
        'CVJPZ0095I',
        {
            company,
            name,
            tag,
            topic: CUSTOMER_TAG_REMOVED.getTopic({tag}),
            user,
        },
    );
}


/**
 * EXPORTS
 */
export {
    subscribeCustomerTagAdded,
    subscribeCustomerTagRemoved,
    unsubscribeCustomerTagAdded,
    unsubscribeCustomerTagRemoved,
};
